import { PrinterOutlined, SnippetsOutlined } from '@ant-design/icons';
import ExceptionOutlined from '@ant-design/icons/lib/icons/ExceptionOutlined';
import { Button } from 'antd';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import { useDepartments } from '@/common/useDepartments';
import type { Order, Order_Position } from '@/generated/graphql-types';

export const Export = ({ orders }: { orders: Order[] }) => {
  const { selectedDepartment } = useDepartments();
  const ordersAreReadyPicking = orders.every((order) => order.status?.name === 'ReadyPicking');

  const renderOrderHeader = (
    doc: jsPDF,
    order: Order,
    yPosition: number,
    pageWidth: number
  ): number => {
    doc.setFontSize(16);
    doc.text(`Order ${order.shortCode || 'N/A'}`, pageWidth / 2, yPosition, {
      align: 'center',
    });
    return yPosition + 8;
  };

  const renderDriverInfo = (
    doc: jsPDF,
    order: Order,
    yPosition: number,
    pageMargin: number,
    lineSpacing: number
  ): number => {
    doc.setFontSize(12);
    yPosition += 10;
    doc.text('Pick up from:', pageMargin, yPosition);
    yPosition += lineSpacing;
    doc.text(`Driver: ${order.driver?.name || 'N/A'}`, pageMargin, yPosition);
    yPosition += lineSpacing;
    doc.text(`Username: ${order.driver?.email || 'N/A'}`, pageMargin, yPosition);
    yPosition += lineSpacing;
    doc.text(
      `Created date: ${
        order.createdAt
          ? dayjs(order.createdAt)
              .utcOffset(selectedDepartment?.timeZone || 0)
              .format('YYYY-MM-DD HH:mm')
          : 'N/A'
      }`,
      pageMargin,
      yPosition
    );
    yPosition += lineSpacing;
    doc.text(
      `Planned date: ${
        order.plannedDate
          ? getOrderPlannedDateText({
              plannedDate: order.plannedDate ?? '',
              plannedDateDuration: order.plannedDateDuration ?? undefined,
              offset: selectedDepartment?.timeZone || 0,
            })
          : 'N/A'
      }`,
      pageMargin,
      yPosition
    );
    return yPosition + 8;
  };

  const renderClientInfo = (
    doc: jsPDF,
    order: Order,
    yPosition: number,
    pageMargin: number,
    pageWidth: number,
    cellWidth: number,
    lineSpacing: number,
    smallSpacing: number
  ): number => {
    doc.line(pageMargin, yPosition, pageWidth + pageMargin, yPosition);
    yPosition += smallSpacing;

    const client = order.client || {};
    const address = client.address || {};

    doc.setFontSize(16);
    yPosition += 8;
    doc.text('Client', pageWidth / 2, yPosition, { align: 'center' });

    doc.setFontSize(12);
    yPosition += lineSpacing;
    doc.text(`Fullname: ${client.fullname || 'N/A'}`, pageMargin, yPosition);
    yPosition += lineSpacing;
    doc.text(`District: ${(address as any).district || 'N/A'}`, pageMargin, yPosition);
    doc.text(`City: ${(address as any).city || 'N/A'}`, pageMargin + cellWidth, yPosition);
    doc.text(
      `Country: ${(address as any).country || 'N/A'}`,
      pageMargin + 2 * cellWidth,
      yPosition
    );
    yPosition += lineSpacing;
    doc.text(`Street: ${(address as any).street || 'N/A'}`, pageMargin, yPosition);

    const comments = (address as any).comments || 'N/A';
    const wrappedComments = doc.splitTextToSize(`Comments: ${comments}`, pageWidth);
    yPosition += lineSpacing;
    doc.text(wrappedComments, pageMargin, yPosition);
    return yPosition + wrappedComments.length * 5;
  };

  const renderProductsHeader = (
    doc: jsPDF,
    yPosition: number,
    pageMargin: number,
    cellWidth: number
  ): number => {
    doc.setFillColor(180, 200, 255);
    doc.rect(pageMargin, yPosition, cellWidth, 10, 'F');
    doc.rect(pageMargin + cellWidth, yPosition, cellWidth, 10, 'F');
    doc.rect(pageMargin + 2 * cellWidth, yPosition, cellWidth, 10, 'F');

    doc.setTextColor(0);
    doc.text('PRODUCTO', pageMargin + cellWidth / 2, yPosition + 7, { align: 'center' });
    doc.text('CANTIDAD', pageMargin + cellWidth + cellWidth / 2, yPosition + 7, {
      align: 'center',
    });
    doc.text('PRECIO', pageMargin + 2 * cellWidth + cellWidth / 2, yPosition + 7, {
      align: 'center',
    });
    return yPosition + 15;
  };

  const renderPositions = (
    doc: jsPDF,
    positions: Order_Position[],
    yPosition: number,
    pageMargin: number,
    cellWidth: number
  ): number => {
    positions.forEach((position: Order_Position) => {
      const productName = position?.product?.name || 'N/A';
      const wrappedProductName = doc.splitTextToSize(productName, cellWidth - 10);
      const quantity =
        position.quantity !== undefined && position.quantity !== null
          ? position.quantity.toString()
          : 'N/A';
      const totalPrice =
        position.totalPrice !== undefined && position.totalPrice !== null
          ? position.totalPrice.toString()
          : 'N/A';

      doc.text(wrappedProductName, pageMargin + cellWidth / 2, yPosition + 2, {
        align: 'center',
      });
      doc.text(quantity, pageMargin + cellWidth + cellWidth / 2, yPosition + 2, {
        align: 'center',
      });
      doc.text(totalPrice, pageMargin + 2 * cellWidth + cellWidth / 2, yPosition + 2, {
        align: 'center',
      });

      yPosition += wrappedProductName.length * 5 + 4;
    });
    return yPosition;
  };

  const renderTotal = (
    doc: jsPDF,
    totalCents: string,
    yPosition: number,
    pageMargin: number,
    pageWidth: number,
    cellWidth: number,
    mediumSpacing: number
  ): number => {
    yPosition += mediumSpacing;

    doc.setFillColor(180, 200, 255);
    doc.rect(pageMargin, yPosition, cellWidth, 10, 'F');
    doc.rect(pageMargin + cellWidth, yPosition, 2 * cellWidth, 10, 'F');
    doc.setTextColor(0);
    doc.text('TOTAL', pageMargin + 5, yPosition + 7, { align: 'left' });
    doc.text(totalCents, pageMargin + pageWidth - 5, yPosition + 7, { align: 'right' });
    return yPosition + mediumSpacing + 10;
  };

  const renderFooter = (
    doc: jsPDF,
    yPosition: number,
    pageMargin: number,
    pageWidth: number
  ): void => {
    doc.line(pageMargin, yPosition, pageMargin + pageWidth, yPosition);
    doc.line(
      pageMargin + pageWidth / 2,
      yPosition,
      pageMargin + pageWidth / 2,
      doc.internal.pageSize.height - 10
    );

    doc.text('Nota', pageMargin + pageWidth / 4, yPosition + 10, { align: 'center' });
    doc.text('Verification stamp', pageMargin + (3 * pageWidth) / 4, yPosition + 10, {
      align: 'center',
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageMargin = 5;
    const pageWidth = doc.internal.pageSize.width - 2 * pageMargin;
    const cellWidth = pageWidth / 3;
    const smallSpacing = 4;
    const mediumSpacing = 10;
    const lineSpacing = 6;

    orders.forEach((order: Order, orderIndex: number) => {
      let yPosition = 15;

      yPosition = renderOrderHeader(doc, order, yPosition, pageWidth);
      yPosition = renderDriverInfo(doc, order, yPosition, pageMargin, lineSpacing);
      yPosition = renderClientInfo(
        doc,
        order,
        yPosition,
        pageMargin,
        pageWidth,
        cellWidth,
        lineSpacing,
        smallSpacing
      );
      yPosition += mediumSpacing;
      yPosition = renderProductsHeader(doc, yPosition, pageMargin, cellWidth);
      yPosition = renderPositions(doc, order.positions, yPosition, pageMargin, cellWidth);
      yPosition = renderTotal(
        doc,
        order.totalCents !== undefined && order.totalCents !== null
          ? order.totalCents.toString()
          : 'N/A',
        yPosition,
        pageMargin,
        pageWidth,
        cellWidth,
        mediumSpacing
      );
      renderFooter(doc, yPosition, pageMargin, pageWidth);

      if (orderIndex < orders.length - 1) {
        doc.addPage();
      }
    });

    doc.save(orders.length > 1 ? 'Orders.pdf' : `Order-${orders[0].shortCode}.pdf`);
  };

  const getExcelHeaderRow = () => {
    return [
      'Order',
      'Driver',
      'Username',
      'Created Date',
      'Planned Date',
      'Client Fullname',
      'District',
      'Street',
      'City',
      'Country',
      'Comments',
      'Producto',
      'TotalCents',
    ];
  };

  const formatOrderDates = (order: Order) => {
    const createdDate = dayjs(order.createdAt)
      .utcOffset(selectedDepartment?.timeZone || 0)
      .format('YYYY-MM-DD HH:mm');
    const plannedDate = getOrderPlannedDateText({
      plannedDate: order.plannedDate,
      plannedDateDuration: order.plannedDateDuration ?? undefined,
      offset: selectedDepartment?.timeZone || 0,
    });
    return { createdDate, plannedDate };
  };

  const formatOrderDriverInfo = (order: Order) => {
    return {
      driverName: order.driver ? order.driver.name : 'N/A',
      driverEmail: order.driver ? order.driver.email : 'N/A',
    };
  };

  const formatOrderClientInfo = (order: Order) => {
    return {
      clientName: order.client.fullname,
      district: order.client.address?.district,
      street: order.client.address?.street,
      city: order.client.address?.city,
      country: order.client.address?.country,
      comments: order.client.address?.comments || '',
    };
  };

  const formatProductDetails = (positions: Order_Position[]) => {
    return positions
      .map(
        (position: Order_Position) =>
          `Nombre: ${position.product.name}\nCantidad: ${position.quantity}\nPrecio: ${position.totalPrice}\n`
      )
      .join('\n');
  };

  const convertOrderToExcelRow = (order: Order) => {
    const { createdDate, plannedDate } = formatOrderDates(order);
    const { driverName, driverEmail } = formatOrderDriverInfo(order);
    const { clientName, district, street, city, country, comments } = formatOrderClientInfo(order);
    const productDetails = formatProductDetails(order.positions);

    return [
      order.shortCode ?? '',
      driverName ?? '',
      driverEmail ?? '',
      createdDate ?? '',
      plannedDate ?? '',
      clientName ?? '',
      district ?? '',
      street ?? '',
      city ?? '',
      country ?? '',
      comments,
      productDetails,
      String(order.totalCents),
    ];
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Orders Data');

    worksheet.addRow(getExcelHeaderRow());

    orders.forEach((order) => {
      worksheet.addRow(convertOrderToExcelRow(order));
    });

    const columnWidth = 11;
    const headerRow = getExcelHeaderRow();
    worksheet.columns = headerRow.map(() => ({ width: columnWidth }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = orders.length > 1 ? 'Orders.xlsx' : `Order-${orders[0].shortCode}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getProviderInfo = (order: Order) => {
    const countryRaw = order?.department?.country?.id || order.client?.address?.country || '';
    const country = countryRaw.toString().toLowerCase();

    if (country === 'cl') {
      return {
        name: 'SOCIEDAD COMERCIALIZADORA JILARITO SPA',
        website: 'https://jilarito.cl/',
      };
    }
    if (country === 'co') {
      return {
        name: 'HILARTE ORGANIC SAS',
        website: 'https://hilarte.co/',
      };
    }

    return {
      name: 'HILART SOCIEDAD ANONIMA CERRADA - HILART S.A.C.',
      website: 'https://hilart.pe/',
    };
  };

  const formatStickerDate = (date: string | null | undefined): string => {
    if (!date) return 'N/A';
    return dayjs(date)
      .utcOffset(selectedDepartment?.timeZone || 0)
      .format('YYYY-MM-DD HH:mm');
  };

  const formatStickerPlannedDate = (order: Order): string => {
    if (!order.plannedDate) return 'N/A';
    return getOrderPlannedDateText({
      plannedDate: order.plannedDate ?? '',
      plannedDateDuration: order.plannedDateDuration ?? undefined,
      offset: selectedDepartment?.timeZone || 0,
    });
  };

  const renderStickerDataSection = (
    _doc: jsPDF,
    order: Order,
    yPosition: number,
    drawSectionHeader: (x: number, y: number, width: number, text: string) => number,
    drawRow: (
      x: number,
      y: number,
      col1Width: number,
      col2Width: number,
      label: string,
      value: string
    ) => number
  ): number => {
    yPosition += drawSectionHeader(5, yPosition, 90, 'DATA');
    yPosition += drawRow(5, yPosition, 45, 45, 'Short code', `${order.shortCode || 'N/A'}`);
    yPosition += drawRow(5, yPosition, 45, 45, 'Pick up from', 'Los Jardines 395, Ñuñoa');
    yPosition += drawRow(5, yPosition, 45, 45, 'Driver', 'Rappi External Driver');
    yPosition += drawRow(5, yPosition, 45, 45, 'Created date', formatStickerDate(order.createdAt));
    yPosition += drawRow(5, yPosition, 45, 45, 'Planned date', formatStickerPlannedDate(order));
    return yPosition + 5;
  };

  const renderStickerClientSection = (
    _doc: jsPDF,
    order: Order,
    yPosition: number,
    drawSectionHeader: (x: number, y: number, width: number, text: string) => number,
    drawRow: (
      x: number,
      y: number,
      col1Width: number,
      col2Width: number,
      label: string,
      value: string
    ) => number
  ): number => {
    yPosition += drawSectionHeader(5, yPosition, 90, 'CLIENT');
    yPosition += drawRow(5, yPosition, 45, 45, 'Full name', `${order.client?.fullname || 'N/A'}`);
    yPosition += drawRow(
      5,
      yPosition,
      45,
      45,
      'District',
      `${order.client?.address?.district || 'N/A'}`
    );
    yPosition += drawRow(
      5,
      yPosition,
      45,
      45,
      'Address',
      `${order.client?.address?.street || 'N/A'}`
    );
    yPosition += drawRow(
      5,
      yPosition,
      45,
      45,
      'Comments',
      `${order.client?.address?.comments || 'N/A'}`
    );
    return yPosition + 5;
  };

  const renderStickerOrderPositions = (
    doc: jsPDF,
    positions: Order_Position[],
    yPosition: number,
    drawSectionHeader: (x: number, y: number, width: number, text: string) => number,
    drawCell: (
      x: number,
      y: number,
      width: number,
      text: string,
      align?: 'left' | 'center' | 'right'
    ) => number
  ): number => {
    yPosition += drawSectionHeader(5, yPosition, 90, 'ORDER');

    const headerHeight = Math.max(
      drawCell(5, yPosition, 30, 'Product', 'center'),
      drawCell(35, yPosition, 30, 'Quantity', 'center'),
      drawCell(65, yPosition, 30, 'Total price', 'center')
    );
    doc.rect(5, yPosition, 30, headerHeight);
    doc.rect(35, yPosition, 30, headerHeight);
    doc.rect(65, yPosition, 30, headerHeight);
    yPosition += headerHeight;

    positions.forEach((position: Order_Position) => {
      const productHeight = drawCell(
        5,
        yPosition,
        30,
        `${position.product?.name || 'N/A'}`,
        'center'
      );
      const quantityHeight = drawCell(35, yPosition, 30, `${position.quantity || 'N/A'}`, 'center');
      const priceHeight = drawCell(65, yPosition, 30, `${position.totalPrice}`, 'center');
      const rowHeight = Math.max(productHeight, quantityHeight, priceHeight);
      doc.rect(5, yPosition, 30, rowHeight);
      doc.rect(35, yPosition, 30, rowHeight);
      doc.rect(65, yPosition, 30, rowHeight);
      yPosition += rowHeight;
    });

    return yPosition;
  };

  const renderStickerOrderTotals = (
    doc: jsPDF,
    order: Order,
    yPosition: number,
    drawCell: (
      x: number,
      y: number,
      width: number,
      text: string,
      align?: 'left' | 'center' | 'right'
    ) => number
  ): number => {
    const itemsTotal = order.positions.reduce(
      (sum: number, p: Order_Position) => sum + (p.totalPrice ?? 0),
      0
    );
    const deliveryCost =
      order.totalCents !== undefined && order.totalCents !== null
        ? order.totalCents - itemsTotal
        : 0;

    if (deliveryCost > 0) {
      const delLabelHeight = drawCell(5, yPosition, 60, 'Delivery cost', 'left');
      const delPriceHeight = drawCell(65, yPosition, 30, `${deliveryCost}`, 'center');
      const delRowHeight = Math.max(delLabelHeight, delPriceHeight);
      doc.rect(5, yPosition, 60, delRowHeight);
      doc.rect(65, yPosition, 30, delRowHeight);
      yPosition += delRowHeight;
    }

    const totalCentsText =
      order.totalCents !== undefined && order.totalCents !== null
        ? order.totalCents.toString()
        : 'N/A';
    const totalLabelHeight = drawCell(5, yPosition, 60, 'Total', 'left');
    const totalPriceHeight = drawCell(65, yPosition, 30, `${totalCentsText}`, 'center');
    const totalRowHeight = Math.max(totalLabelHeight, totalPriceHeight);
    doc.rect(5, yPosition, 60, totalRowHeight);
    doc.rect(65, yPosition, 30, totalRowHeight);
    yPosition += totalRowHeight;

    return yPosition + 5;
  };

  const renderStickerProviderSection = (
    _doc: jsPDF,
    order: Order,
    yPosition: number,
    drawSectionHeader: (x: number, y: number, width: number, text: string) => number,
    drawRow: (
      x: number,
      y: number,
      col1Width: number,
      col2Width: number,
      label: string,
      value: string
    ) => number
  ): number => {
    yPosition += drawSectionHeader(5, yPosition, 90, 'PROVIDER');
    const providerInfo = getProviderInfo(order);
    yPosition += drawRow(5, yPosition, 90, 0, providerInfo.name, '');
    yPosition += drawRow(5, yPosition, 90, 0, `Web page: ${providerInfo.website}`, '');
    return yPosition;
  };

  const exportToStickers = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [100, 150],
    });

    const drawCell = (
      x: number,
      y: number,
      width: number,
      text: string,
      align: 'left' | 'center' | 'right' = 'left'
    ): number => {
      doc.setFontSize(8);
      const textLines: string[] = doc.splitTextToSize(text, width - 2);
      const textHeight = textLines.length * 3;
      textLines.forEach((line, index) => {
        const textX =
          align === 'center' ? x + width / 2 : align === 'right' ? x + width - 1 : x + 1;
        doc.text(line, textX, y + 3 + index * 3, { align });
      });
      return textHeight + 2;
    };

    const drawRow = (
      x: number,
      y: number,
      col1Width: number,
      col2Width: number,
      label: string,
      value: string
    ): number => {
      const labelHeight = drawCell(x, y, col1Width, label);
      const valueHeight = drawCell(x + col1Width, y, col2Width, value);
      const rowHeight = Math.max(labelHeight, valueHeight);
      doc.rect(x, y, col1Width, rowHeight);
      doc.rect(x + col1Width, y, col2Width, rowHeight);
      return rowHeight;
    };

    const drawSectionHeader = (x: number, y: number, width: number, text: string): number => {
      doc.setFontSize(8);
      doc.setFont('bold');
      doc.text(text, x + width / 2, y + 3, { align: 'center' });
      doc.setFont('normal');
      return 5;
    };

    orders.forEach((order: Order, orderIndex: number) => {
      let yPosition = 10;
      doc.setFontSize(8);

      yPosition = renderStickerDataSection(doc, order, yPosition, drawSectionHeader, drawRow);
      yPosition = renderStickerClientSection(doc, order, yPosition, drawSectionHeader, drawRow);
      yPosition = renderStickerOrderPositions(
        doc,
        order.positions,
        yPosition,
        drawSectionHeader,
        drawCell
      );
      yPosition = renderStickerOrderTotals(doc, order, yPosition, drawCell);
      yPosition = renderStickerProviderSection(doc, order, yPosition, drawSectionHeader, drawRow);

      if (orderIndex < orders.length - 1) {
        doc.addPage();
      }
    });

    doc.save('Stickers.pdf');
  };
  return (
    <>
      <Button type="primary" shape="circle" icon={<PrinterOutlined />} onClick={exportToPDF} />
      <Button icon={<ExceptionOutlined />} type="primary" shape="circle" onClick={exportToExcel} />
      {ordersAreReadyPicking && (
        <Button
          icon={<SnippetsOutlined />}
          type="primary"
          shape="circle"
          onClick={exportToStickers}
        />
      )}
    </>
  );
};

const getOrderPlannedDateText = ({
  plannedDate,
  plannedDateDuration,
  offset,
}: {
  plannedDate: string;
  plannedDateDuration?: number;
  offset: number;
}): string => {
  const start = dayjs(plannedDate).utcOffset(offset);
  let text = start.format('YYYY-MM-DD HH:mm');
  if (plannedDateDuration) {
    const end = start.add(plannedDateDuration, 'minutes');
    text += `-${end.format('HH:mm')}`;
  }
  return text;
};
